import React, { useEffect, useState } from "react";
import { Zap, FlaskConical, AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import api from "@/lib/api";

/**
 * StripeModeTile — one-glance LIVE / TEST / UNCONFIGURED badge for the
 * admin dashboard. Hits GET /api/admin/stripe-mode (platform-admin
 * only) which inspects STRIPE_API_KEY[:7] on the backend — no Stripe
 * round-trips. Hides on 403.
 */
export const StripeModeTile = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = (announce = false) => {
        if (announce) setRefreshing(true);
        api.get("/admin/stripe-mode")
            .then((r) => setData(r.data))
            .catch(() => { /* 403 hides tile */ })
            .finally(() => { setLoading(false); setRefreshing(false); });
    };
    useEffect(() => { load(); /* eslint-disable-line */ }, []);

    if (loading) {
        return (
            <div className="card-soft p-5" data-testid="stripe-mode-loading">
                <Loader2 size={16} className="animate-spin text-emerald-500" />
            </div>
        );
    }
    if (!data) return null;

    const mode = (data.mode || "unconfigured").toLowerCase();
    const prefix = data.key_prefix || "—";

    const presets = {
        live: {
            Icon: Zap,
            iconClass: "bg-rose-500/10 text-rose-600",
            pillClass: "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/40",
            pillLabel: "LIVE",
            borderClass: "border-rose-500/40",
            heading: "Charging real cards",
            sub: "Every /billing/checkout hits production Stripe.",
        },
        test: {
            Icon: FlaskConical,
            iconClass: "bg-emerald-500/10 text-emerald-600",
            pillClass: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/40",
            pillLabel: "TEST",
            borderClass: "border-transparent",
            heading: "Sandbox mode — safe to click",
            sub: "Use card 4242 4242 4242 4242, any future expiry/CVC.",
        },
        unconfigured: {
            Icon: AlertTriangle,
            iconClass: "bg-amber-500/10 text-amber-600",
            pillClass: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/40",
            pillLabel: "UNCONFIGURED",
            borderClass: "border-amber-500/50",
            heading: "Stripe not wired",
            sub: "STRIPE_API_KEY is missing or malformed in backend/.env.",
        },
    };
    const p = presets[mode] || presets.unconfigured;
    const { Icon } = p;

    return (
        <div
            className={`card-soft p-5 border-2 ${p.borderClass}`}
            data-testid="stripe-mode-tile"
            data-mode={mode}
        >
            <div className="flex items-center gap-2 mb-3">
                <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${p.iconClass}`}>
                    <Icon size={16} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Stripe mode
                    </div>
                    <div className="text-sm font-heading font-semibold text-foreground flex items-center gap-2 mt-0.5">
                        <span
                            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wider ${p.pillClass}`}
                            data-testid="stripe-mode-pill"
                        >
                            {p.pillLabel}
                        </span>
                        <span className="truncate text-muted-foreground font-normal">{p.heading}</span>
                    </div>
                </div>
                <button
                    onClick={() => load(true)}
                    disabled={refreshing}
                    className="rounded-full border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted inline-flex items-center gap-1"
                    data-testid="stripe-mode-refresh"
                    title="Re-check"
                >
                    {refreshing ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                </button>
            </div>
            <p className="text-xs text-muted-foreground leading-snug" data-testid="stripe-mode-sub">
                {p.sub}
            </p>
            <p className="mt-3 text-[10px] text-muted-foreground font-mono truncate" title={prefix} data-testid="stripe-mode-prefix">
                key {prefix}…
            </p>
        </div>
    );
};

export default StripeModeTile;
